<template>
  <div class="sectors-list">
    <div class="toolbar">
      <input
        v-model="search"
        type="text"
        class="filter-input"
        :placeholder="$t('bo.esco.searchSectors')"
      />
      <span class="toolbar-info">
        {{ $t('bo.esco.sectorsToolbarInfo', { groups: filteredGroups.length, sectors: totalSectors, mappings: totalMappings }) }}
      </span>
    </div>

    <div v-if="loading" class="loading">{{ $t('bo.esco.loadingSectors') }}</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="filteredGroups.length === 0" class="empty">{{ $t('bo.esco.noSectorGroupsFound') }}</div>

    <table v-else class="table">
      <thead>
        <tr>
          <th>{{ $t('bo.esco.colSectorGroup') }}</th>
          <th>{{ $t('bo.esco.colEscoOccupations') }}</th>
          <th>{{ $t('bo.esco.colAutoMapped') }}</th>
          <th>{{ $t('bo.esco.colManual') }}</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="group in filteredGroups" :key="group.id">
          <!-- Group header -->
          <tr class="group-row" @click="toggleExpand(group.id)">
            <td class="col-name">
              <span class="expand-icon">{{ expandedIds.has(group.id) ? '▾' : '▸' }}</span>
              <span class="group-title">{{ group.name }}</span>
              <span class="count-badge count-group">{{ group.sectors.length }}</span>
            </td>
            <td colspan="3">
              <span class="text-muted">{{ $t('bo.esco.totalOccupations', { count: group.totalOccupations }) }}</span>
            </td>
          </tr>

          <!-- Expanded: sectors -->
          <template v-if="expandedIds.has(group.id)">
            <tr
              v-for="sector in group.sectors"
              :key="sector.id"
              class="sector-row"
            >
              <td class="child-name">
                <span class="child-indent">└</span>
                {{ sector.name }}
              </td>
              <td>
                <span class="count-badge count-active">{{ sector.occupationsCount }}</span>
              </td>
              <td>
                <span v-if="sector.onetCount > 0" class="count-badge count-onet">{{ sector.onetCount }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <span v-if="sector.manualCount > 0" class="count-badge count-manual">{{ sector.manualCount }}</span>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import escoService from '@/services/escoService'

const { t } = useI18n()
import api from '@/services/api'

const loading = ref(false)
const error = ref('')
const search = ref('')
const expandedIds = ref(new Set())

// sectorGroups: [{ id, name, sectors: [{ id, name }] }]
const sectorGroups = ref([])

// occupationSectorStats: { sectorId: { total, onet, manual } }
const sectorStats = ref({})

const loadData = async () => {
  loading.value = true
  error.value = ''
  try {
    // Load sector groups with nested sectors
    const res = await escoService.getSectorGroups()
    const groups = res.data || []

    // Load occupation-sector stats from Layer 1
    const statsRes = await api.get('/admin/esco/sector-stats')
    const stats = statsRes.data?.data || {}
    sectorStats.value = stats

    // Enrich sectors with stats
    sectorGroups.value = groups.map(g => ({
      ...g,
      sectors: (g.sectors || []).map(s => ({
        ...s,
        occupationsCount: stats[s.id]?.total ?? 0,
        onetCount: stats[s.id]?.onet ?? 0,
        manualCount: stats[s.id]?.manual ?? 0,
      })),
      totalOccupations: (g.sectors || []).reduce((sum, s) => sum + (stats[s.id]?.total ?? 0), 0),
    }))
  } catch {
    // Stats endpoint may not exist yet — load groups without stats
    try {
      const res = await escoService.getSectorGroups()
      sectorGroups.value = (res.data || []).map(g => ({
        ...g,
        sectors: (g.sectors || []).map(s => ({ ...s, occupationsCount: 0, onetCount: 0, manualCount: 0 })),
        totalOccupations: 0,
      }))
    } catch {
      error.value = t('bo.esco.failedLoadSectors')
    }
  } finally {
    loading.value = false
  }
}

const filteredGroups = computed(() => {
  if (!search.value.trim()) return sectorGroups.value
  const q = search.value.toLowerCase()
  return sectorGroups.value
    .map(g => ({
      ...g,
      sectors: g.sectors.filter(s => s.name.toLowerCase().includes(q)),
    }))
    .filter(g => g.name.toLowerCase().includes(q) || g.sectors.length > 0)
})

const totalSectors = computed(() =>
  sectorGroups.value.reduce((sum, g) => sum + g.sectors.length, 0)
)

const totalMappings = computed(() =>
  sectorGroups.value.reduce((sum, g) => sum + g.totalOccupations, 0)
)

const toggleExpand = (id) => {
  const next = new Set(expandedIds.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expandedIds.value = next
}

onMounted(loadData)
</script>

<style scoped>
.sectors-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.filter-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  min-width: 260px;
}
.filter-input:focus { outline: none; border-color: #3b82f6; }

.toolbar-info { font-size: 0.8rem; color: #6b7280; margin-left: auto; }

.loading, .empty { padding: 3rem; text-align: center; color: #6b7280; }
.error-msg { padding: 1rem 1.25rem; color: #dc2626; }

.table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.group-row {
  cursor: pointer;
  background: #fafafa;
}
.group-row:hover td { background: #f0f9ff; }
.group-row td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
}

.sector-row td {
  padding: 0.5rem 1rem 0.5rem 2.5rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.825rem;
  color: #374151;
}
.sector-row:hover td { background: #fafbff; }

.col-name { display: flex; align-items: center; gap: 0.5rem; }
.expand-icon { font-size: 0.75rem; color: #6b7280; width: 1rem; }
.group-title { color: #111827; }

.child-name { display: flex; align-items: center; gap: 0.5rem; }
.child-indent { color: #9ca3af; font-size: 0.9rem; }

.count-badge {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}
.count-group  { background: #e0e7ff; color: #3730a3; }
.count-active { background: #d1fae5; color: #065f46; }
.count-onet   { background: #dbeafe; color: #1e40af; }
.count-manual { background: #fce7f3; color: #9d174d; }

.text-muted { color: #9ca3af; font-size: 0.8rem; }
</style>
